import { infoCircle, printerIco } from '@/assets/icons'
import SessionCounter from '@/components/molecules/sessionCounter/sessionCounter'
import { useTranslate } from '@/i18n/useTranslate'
import { formatPromotionTotalAmount } from '@/utilities/depositBonusHelper'
import { PrintSlipRequest } from '@/utilities/posSlipHelper'
import { normalizePromoCode } from '@/utilities/referralHelper'

type TransferStatus = 'idle' | 'success' | 'error'

type SuccessDetails = {
    date?: string
    time?: string
    referenceNo?: string
    location?: string
    amount?: string
    deviceName?: string
}

type DirectBettingConfirmationTemplateProps = {
    vendorName: string
    vendorLogoUrl?: string | null
    accountId: string
    amount: number
    currency?: string
    transferStatus?: TransferStatus
    failedAttempts?: number
    maxAttempts?: number
    onConfirm: () => void
    onChangeData: () => void
    onFinish: () => void
    onSessionTimeout?: () => void
    onShowVoucher: () => void
    onPrintConfirmation?: () => void
    isSubmitting?: boolean
    isCheckingEligibility?: boolean
    awaitingPromoCode?: boolean
    promoCode?: string
    onPromoCodeChange?: (code: string) => void
    successDetails?: SuccessDetails
    posSlip?: PrintSlipRequest | null
    sessionId?: string
    bonusAmount?: number
}

const ReceiptIcon = () => (
    <svg viewBox="0 0 32 32" aria-hidden="true">
        <path
            d="M8 5.5h16v21l-2.2-1.4-2.2 1.4-2.2-1.4-1.4 1.4-1.4-1.4-2.2 1.4-2.2-1.4-2.2 1.4V5.5z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path
            d="M12 11h8M12 15.5h8M12 20h5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
)

const PROMO_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('')

export default function DirectBettingConfirmationTemplate({
    vendorName,
    vendorLogoUrl,
    accountId,
    amount,
    currency = 'RSD',
    transferStatus = 'idle',
    failedAttempts = 0,
    maxAttempts = 3,
    onConfirm,
    onChangeData,
    onFinish,
    onSessionTimeout,
    onShowVoucher,
    onPrintConfirmation,
    isSubmitting = false,
    isCheckingEligibility = false,
    awaitingPromoCode = false,
    promoCode = '',
    onPromoCodeChange,
    successDetails,
    posSlip = null,
    bonusAmount = 0
}: DirectBettingConfirmationTemplateProps) {
    const { t } = useTranslate()
    const clampedFailedAttempts = Math.min(
        Math.max(failedAttempts, 0),
        maxAttempts
    )
    const clampedAttempt = Math.min(clampedFailedAttempts + 1, maxAttempts)
    const formattedAmount = Math.round(amount).toLocaleString('en-US')
    const isSuccess = transferStatus === 'success'
    const isError = transferStatus === 'error'
    const isInitialAttempt =
        clampedFailedAttempts === 0 && transferStatus === 'idle'
    const isLoading =
        isCheckingEligibility ||
        (isSubmitting && transferStatus === 'idle') ||
        (isInitialAttempt && !awaitingPromoCode && !isCheckingEligibility)
    const showVoucherOption = clampedFailedAttempts >= 1
    const canRetry = clampedFailedAttempts < maxAttempts
    const showConfirmButton =
        awaitingPromoCode || (canRetry && clampedFailedAttempts > 0)
    const showSecondaryPair = clampedFailedAttempts === 2 && !awaitingPromoCode
    const isFinalFailure = isError && clampedFailedAttempts >= maxAttempts

    const panelClass = isLoading
        ? 'direct-betting-confirmation__info direct-betting-confirmation__info--loading'
        : isError
          ? 'direct-betting-confirmation__info direct-betting-confirmation__info--error'
          : 'direct-betting-confirmation__info'

    const titleText = t('directBettingConfirmation.title')
    const paidAmount = Math.max(0, amount)
    const rewardAmount = Math.max(0, bonusAmount)
    const creditedAmount = paidAmount + rewardAmount
    const hasBonus = rewardAmount > 0

    if (isSuccess) {
        const merchantLine = [successDetails?.location, successDetails?.deviceName]
            .filter(Boolean)
            .join(' • ')
        const transactionLine = [
            successDetails?.referenceNo
                ? `#${successDetails.referenceNo}`
                : null,
            posSlip?.terminalId ? `#${posSlip.terminalId}` : null,
            posSlip?.cardNumber,
            posSlip?.rrn
        ]
            .filter(Boolean)
            .join(' • ')
        const dateLine = [successDetails?.date, successDetails?.time]
            .filter(Boolean)
            .join(' u ')

        return (
            <div className="direct-betting-confirmation direct-betting-confirmation--success">
                <div className="direct-betting-confirmation__hero">
                    <div className="direct-betting-confirmation__state-icon direct-betting-confirmation__state-icon--success">
                        ✓
                    </div>
                    <h1>{t('directBettingConfirmation.successHeadline')}</h1>
                </div>

                <section className="direct-betting-confirmation__review">
                    <h2>{t('directBettingConfirmation.paymentOverview')}</h2>
                    <div className="direct-betting-confirmation__total-pill">
                        <strong>
                            {formatPromotionTotalAmount(creditedAmount)}{' '}
                            {currency}
                        </strong>
                        {hasBonus ? (
                            <p>
                                <span>
                                    {formatPromotionTotalAmount(paidAmount)}{' '}
                                    {currency} +
                                </span>
                                <span className="direct-betting-confirmation__bonus-pill">
                                    {t('directBettingConfirmation.bonusAmount', {
                                        amount: formatPromotionTotalAmount(
                                            rewardAmount
                                        ),
                                        currency
                                    })}
                                </span>
                            </p>
                        ) : null}
                    </div>

                    <div className="direct-betting-confirmation__review-vendor">
                        {vendorLogoUrl ? (
                            <img src={vendorLogoUrl} alt={vendorName} />
                        ) : (
                            <span>{vendorName.slice(0, 2).toUpperCase()}</span>
                        )}
                    </div>

                    <p className="direct-betting-confirmation__account-label">
                        {t('directBettingConfirmation.orderIdLabel')}
                    </p>
                    <div className="direct-betting-confirmation__account-value">
                        {accountId}
                    </div>
                </section>

                <section className="direct-betting-confirmation__purchase">
                    <div className="direct-betting-confirmation__purchase-icon">
                        <ReceiptIcon />
                    </div>
                    <div className="direct-betting-confirmation__purchase-copy">
                        <strong>
                            {t('directBettingConfirmation.purchaseDetails')}
                        </strong>
                        {merchantLine ? <p>{merchantLine}</p> : null}
                        {transactionLine ? <p>{transactionLine}</p> : null}
                        {dateLine ? <p>{dateLine}</p> : null}
                    </div>
                    {onPrintConfirmation ? (
                        <button
                            type="button"
                            className="direct-betting-confirmation__print"
                            onClick={onPrintConfirmation}>
                            <img src={printerIco} alt="" />
                            {t(
                                'directBettingConfirmation.printConfirmationButton'
                            )}
                        </button>
                    ) : null}
                </section>

                <div className="direct-betting-confirmation__actions">
                    {onSessionTimeout ? (
                        <div className="direct-betting-confirmation__session-counter">
                            <SessionCounter onEndSession={onSessionTimeout} />
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className="direct-betting-confirmation__finish"
                        onClick={onFinish}>
                        {t('directBettingConfirmation.finishButton')}
                    </button>
                </div>
            </div>
        )
    }

    const vendorCards = (
        <div className="direct-betting-confirmation__details">
            <div className="direct-betting-confirmation__vendor">
                {vendorLogoUrl ? (
                    <img src={vendorLogoUrl} alt={vendorName} />
                ) : (
                    <div className="direct-betting-confirmation__vendor-fallback">
                        {vendorName.slice(0, 2).toUpperCase()}
                    </div>
                )}
            </div>

            <div className="direct-betting-confirmation__card">
                <p>{t('directBettingConfirmation.accountIdLabel')}</p>
                <strong>{accountId}</strong>
            </div>

            <div className="direct-betting-confirmation__card">
                <p>{t('directBettingConfirmation.amountLabel')}</p>
                <strong className="direct-betting-confirmation__amount">
                    {formattedAmount} <span>{currency}</span>
                </strong>
            </div>
        </div>
    )

    return (
        <div className="direct-betting-confirmation">
            <div className="direct-betting-confirmation__top">
                <p className="direct-betting-confirmation__status-label">
                    {t('directBettingConfirmation.statusLabel')}
                </p>
                <div className="direct-betting-confirmation__attempt-pill">
                    {t('directBettingConfirmation.attemptLabel', {
                        attempt: clampedAttempt,
                        total: maxAttempts
                    })}
                </div>
            </div>

            <div className="direct-betting-confirmation__progress">
                {Array.from({ length: maxAttempts }).map((_, index) => (
                    <span
                        key={index}
                        className={`direct-betting-confirmation__progress-segment ${
                            transferStatus === 'success' &&
                            index === clampedAttempt - 1
                                ? 'direct-betting-confirmation__progress-segment--success'
                                : index < clampedFailedAttempts
                                  ? 'direct-betting-confirmation__progress-segment--error'
                                  : index === clampedAttempt - 1
                                    ? 'direct-betting-confirmation__progress-segment--active'
                                    : ''
                        }`}
                    />
                ))}
            </div>

            <h1>{titleText}</h1>

            {vendorCards}

            <div className={panelClass}>
                        {isLoading && (
                            <div className="direct-betting-confirmation__loader" />
                        )}
                        {transferStatus === 'idle' && !isLoading && (
                            <img src={infoCircle} alt="" />
                        )}
                        {transferStatus === 'error' && (
                            <div className="direct-betting-confirmation__state-icon direct-betting-confirmation__state-icon--error">
                                ×
                            </div>
                        )}

                        <h2>
                            {isCheckingEligibility
                                ? t(
                                      'directBettingConfirmation.eligibilityLoadingTitle'
                                  )
                                : isLoading
                                  ? t('directBettingConfirmation.loadingTitle')
                                  : isError
                                    ? t('directBettingConfirmation.errorTitle')
                                    : awaitingPromoCode
                                      ? t(
                                            'directBettingConfirmation.promoTitle'
                                        )
                                      : t(
                                            'directBettingConfirmation.infoTitle'
                                        )}
                        </h2>

                        {isCheckingEligibility ? (
                            <p>
                                {t(
                                    'directBettingConfirmation.eligibilityLoadingText'
                                )}
                            </p>
                        ) : isLoading ? (
                            <p>{t('directBettingConfirmation.loadingText')}</p>
                        ) : isError ? (
                            <>
                                {isFinalFailure ? (
                                    <>
                                        <p className="direct-betting-confirmation__info-strong">
                                            {t(
                                                'directBettingConfirmation.finalErrorText1'
                                            )}
                                        </p>
                                        <p>
                                            {t(
                                                'directBettingConfirmation.finalErrorText2'
                                            )}
                                        </p>
                                        <p>
                                            {t(
                                                'directBettingConfirmation.finalErrorText3'
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p>
                                            {t(
                                                'directBettingConfirmation.errorText1'
                                            )}
                                        </p>
                                        <p className="direct-betting-confirmation__info-strong">
                                            {t(
                                                'directBettingConfirmation.errorText2'
                                            )}
                                        </p>
                                    </>
                                )}
                            </>
                        ) : awaitingPromoCode ? (
                            <>
                                <p>
                                    {t('directBettingConfirmation.promoText')}
                                </p>
                                <p className="direct-betting-confirmation__info-strong">
                                    {t('directBettingConfirmation.promoHint')}
                                </p>
                            </>
                        ) : (
                            <>
                                <p>{t('directBettingConfirmation.infoText1')}</p>
                                <p className="direct-betting-confirmation__info-strong">
                                    {t('directBettingConfirmation.infoText2')}
                                </p>
                            </>
                        )}
                    </div>

                    {awaitingPromoCode && onPromoCodeChange && (
                        <div className="direct-betting-confirmation__promo">
                            <p className="direct-betting-confirmation__promo-label">
                                {t('directBettingConfirmation.promoCodeLabel')}
                            </p>
                            <div className="direct-betting-confirmation__promo-input-wrap">
                                <input
                                    value={promoCode}
                                    readOnly
                                    className="direct-betting-confirmation__promo-input"
                                    placeholder="----"
                                />
                                <button
                                    type="button"
                                    className="direct-betting-confirmation__promo-clear"
                                    onClick={() => onPromoCodeChange('')}
                                    aria-label={t(
                                        'directBettingConfirmation.promoClear'
                                    )}>
                                    ×
                                </button>
                            </div>
                            <div className="direct-betting-confirmation__promo-pad">
                                {PROMO_CHARS.map((char) => (
                                    <button
                                        key={char}
                                        type="button"
                                        className="direct-betting-confirmation__promo-key"
                                        disabled={promoCode.length >= 4}
                                        onClick={() =>
                                            onPromoCodeChange(
                                                normalizePromoCode(
                                                    `${promoCode}${char}`
                                                )
                                            )
                                        }>
                                        {char}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="direct-betting-confirmation__promo-key direct-betting-confirmation__promo-key--wide"
                                    onClick={() =>
                                        onPromoCodeChange(promoCode.slice(0, -1))
                                    }
                                    aria-label={t(
                                        'directBettingConfirmation.promoDelete'
                                    )}>
                                    ⌫
                                </button>
                            </div>
                        </div>
                    )}

            <div className="direct-betting-confirmation__actions">
                <div className="direct-betting-confirmation__primary-actions">
                    {showConfirmButton && (
                        <button
                            className="direct-betting-confirmation__confirm"
                            onClick={onConfirm}
                            disabled={isSubmitting}>
                            {t('directBettingConfirmation.confirmButton')}
                        </button>
                    )}
                    {showVoucherOption && !showSecondaryPair && (
                        <button
                            className="direct-betting-confirmation__show-voucher"
                            onClick={onShowVoucher}
                            disabled={isSubmitting}>
                            {t('directBettingConfirmation.showVoucherButton')}
                        </button>
                    )}
                </div>
                {showSecondaryPair && (
                    <div className="direct-betting-confirmation__secondary-actions">
                        <button
                            className="direct-betting-confirmation__change direct-betting-confirmation__change--inline"
                            onClick={onChangeData}
                            disabled={isSubmitting}>
                            {t('directBettingConfirmation.changeButton')}
                        </button>
                        <button
                            className="direct-betting-confirmation__show-voucher direct-betting-confirmation__show-voucher--inline"
                            onClick={onShowVoucher}
                            disabled={isSubmitting}>
                            {t('directBettingConfirmation.showVoucherButton')}
                        </button>
                    </div>
                )}
                {!showSecondaryPair && (
                    <button
                        className="direct-betting-confirmation__change"
                        onClick={onChangeData}
                        disabled={isSubmitting}>
                        {t('directBettingConfirmation.changeButton')}
                    </button>
                )}
            </div>
        </div>
    )
}
